<script setup lang="ts">
import {
  h,
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  resolveComponent,
  type Component,
} from 'vue'
import AdminAdvertiserDetail from '@/views/AdminAdvertiserDetail.vue'
import AdminAdvertiserForm from '@/views/forms/AdminAdvertiserForm.vue'
import TriStateFilterButtons from '@/components/TriStateFilterButtons.vue'
import { useAdmin } from '@/stores/admin'
import type { PlatformAdvertiser, BrandSectorInfo } from '@/stores/admin'
import type { TableColumn } from '@nuxt/ui'
import { getPaginationRowModel } from '@tanstack/vue-table'
import { handleError } from '@/lib/error'
import { formatPlatformName } from '@/lib/utils'
import { createSortableHeader } from '@/lib/tableHelpers'
import { useTriStateFilter, NO_SECTOR } from '@/composables/useTriStateFilter'
import { useUrlFilters } from '@/composables/useUrlFilters'
import { activeFilterUi } from '@/lib/filterUtils'

type AdvertiserRow = PlatformAdvertiser & { brand_name: string; brand_sectors: BrandSectorInfo[] }

// URL-safe stand-in for country_code = null (an empty value would be dropped from the query string)
const GLOBAL = '__global__'

const admin = useAdmin()
const UBadge = resolveComponent('UBadge')
const UButton = resolveComponent('UButton') as Component
const sortable = createSortableHeader(UButton)

const isLoading = ref(true)
const sorting = ref([{ id: 'brand_name', desc: false }])
// ponytail: client-side paging over the full list; server paging if the table outgrows memory
const pagination = ref({ pageIndex: 0, pageSize: 50 })
const advertisers = ref<PlatformAdvertiser[]>([])

const searchQuery = ref('')
const platformFilter = useTriStateFilter()
const sectorFilter = useTriStateFilter()
// Include-only country filter (codes; GLOBAL for country_code = null), same UI as org setup
const selectedCountryCodes = ref<string[]>([])
const countrySearchQuery = ref('')
const showCountryFilter = ref(false)

useUrlFilters({
  filters: { search: searchQuery, country: selectedCountryCodes },
  triStateFilters: { platform: platformFilter, sector: sectorFilter },
  debounce: 300,
})

const showSlideover = ref(false)
const showCreateModal = ref(false)
const selected = ref<AdvertiserRow | null>(null)

const brandsById = computed(() => new Map(admin.brands.map((b) => [b.id, b])))

const rows = computed<AdvertiserRow[]>(() =>
  advertisers.value.map((a) => {
    const brand = brandsById.value.get(a.brand_id)
    return { ...a, brand_name: brand?.name ?? '', brand_sectors: brand?.sectors ?? [] }
  }),
)

const filteredRows = computed(() => {
  let result = rows.value

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(
      (a) =>
        a.platform_advertiser_identifier.toLowerCase().includes(q) ||
        a.brand_name.toLowerCase().includes(q),
    )
  }

  const inFamily = (a: AdvertiserRow, rootCode: string) =>
    admin.getPlatformFamilyCodes(rootCode).includes(a.platform_code)
  if (platformFilter.included.value.length) {
    result = result.filter((a) => platformFilter.included.value.some((c) => inFamily(a, c)))
  }
  if (platformFilter.excluded.value.length) {
    result = result.filter((a) => !platformFilter.excluded.value.some((c) => inFamily(a, c)))
  }

  const hasSector = (a: AdvertiserRow, id: string) =>
    id === NO_SECTOR ? a.sectors.length === 0 : a.sectors.some((s) => s.id === id)
  if (sectorFilter.included.value.length) {
    result = result.filter((a) => sectorFilter.included.value.some((id) => hasSector(a, id)))
  }
  if (sectorFilter.excluded.value.length) {
    result = result.filter((a) => !sectorFilter.excluded.value.some((id) => hasSector(a, id)))
  }

  if (selectedCountryCodes.value.length) {
    result = result.filter((a) => selectedCountryCodes.value.includes(a.country_code ?? GLOBAL))
  }

  return result
})

watch(
  () => filteredRows.value.length,
  () => {
    pagination.value.pageIndex = 0
  },
)

const platformFilterOptions = computed(() =>
  admin.platforms
    .filter((p) => !p.parent_platform_id)
    .map((p) => ({ label: formatPlatformName(p.name), value: p.code })),
)

const sectorFilterOptions = computed(() => [
  { label: 'No sector', value: NO_SECTOR },
  ...admin.sectors.map((s) => ({ label: s.name, value: s.id, icon: s.emoji })),
])

// Only countries actually used by advertisers
const usedCountries = computed(() => {
  const used = new Set(advertisers.value.map((a) => a.country_code ?? GLOBAL))
  const list = admin.countries
    .filter((c) => used.has(c.code))
    .map((c) => ({ id: c.id, code: c.code, name: c.name_en, emoji: c.emoji }))
  if (used.has(GLOBAL)) list.unshift({ id: GLOBAL, code: GLOBAL, name: 'Global', emoji: '🌍' })
  return list
})

const filteredCountries = computed(() => {
  const q = countrySearchQuery.value.toLowerCase()
  if (!q) return usedCountries.value
  return usedCountries.value.filter(
    (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
  )
})

// Collapsed summary: fully selected groups (largest first, no redundant subsets) + leftover countries
const selectedSummary = computed(() => {
  const selected = new Set(selectedCountryCodes.value)
  const covered = new Set<string>()
  const chips: { key: string; label: string; codes: string[] }[] = []
  for (const g of [...groupCoverage.value].sort((a, b) => b.totalCount - a.totalCount)) {
    if (g.selectedCount !== g.totalCount || g.codes.every((c) => covered.has(c))) continue
    chips.push({ key: `g:${g.id}`, label: g.name, codes: g.codes })
    g.codes.forEach((c) => covered.add(c))
  }
  for (const c of usedCountries.value) {
    if (selected.has(c.code) && !covered.has(c.code)) {
      chips.push({ key: c.code, label: `${c.emoji} ${c.name}`, codes: [c.code] })
    }
  }
  return chips
})

function removeChip(codes: string[]) {
  selectedCountryCodes.value = selectedCountryCodes.value.filter((c) => !codes.includes(c))
}

function toggleCountry(code: string) {
  selectedCountryCodes.value = selectedCountryCodes.value.includes(code)
    ? selectedCountryCodes.value.filter((c) => c !== code)
    : [...selectedCountryCodes.value, code]
}

// Groups reference country ids; restrict each group to countries that have advertisers
const groupCoverage = computed(() => {
  const codeById = new Map(usedCountries.value.map((c) => [c.id, c.code]))
  return admin.countryGroups
    .map((g) => {
      const codes = g.country_ids.flatMap((id) => codeById.get(id) ?? [])
      const selectedCount = codes.filter((c) => selectedCountryCodes.value.includes(c)).length
      return { id: g.id, name: g.name, codes, selectedCount, totalCount: codes.length }
    })
    .filter((g) => g.totalCount > 0)
})

function toggleGroup(group: { codes: string[]; selectedCount: number; totalCount: number }) {
  const set = new Set(selectedCountryCodes.value)
  if (group.selectedCount === group.totalCount) {
    group.codes.forEach((c) => set.delete(c))
  } else {
    group.codes.forEach((c) => set.add(c))
  }
  selectedCountryCodes.value = [...set]
}

const columns: TableColumn<AdvertiserRow>[] = [
  { accessorKey: 'brand_name', header: sortable('Brand'), size: 180 },
  {
    accessorKey: 'platform_name',
    header: sortable('Platform'),
    size: 120,
    cell: ({ row }) => formatPlatformName(row.original.platform_name),
  },
  {
    accessorKey: 'platform_advertiser_identifier',
    header: sortable('Identifier'),
    size: 220,
    cell: ({ row }) =>
      h('span', { class: 'font-mono text-sm' }, row.original.platform_advertiser_identifier),
  },
  {
    accessorKey: 'country_name',
    header: sortable('Country'),
    size: 120,
    cell: ({ row }) => row.original.country_name ?? h('span', { class: 'text-muted' }, 'Global'),
  },
  {
    accessorKey: 'search_type',
    header: 'Type',
    size: 100,
    cell: ({ row }) =>
      row.original.search_type === 'advertiser_name'
        ? h(UBadge, { color: 'neutral', variant: 'subtle', size: 'sm' }, () => 'name')
        : null,
  },
  {
    id: 'sectors',
    header: 'Sectors',
    cell: ({ row }) => {
      // Show all brand sectors: effective ones styled by source, excluded ones with line-through
      const effectiveIds = new Set(row.original.sectors.map((s) => s.id))
      const chips = row.original.brand_sectors.map((s) => {
        const effective = row.original.sectors.find((e) => e.id === s.id)
        const excluded = !effectiveIds.has(s.id)
        return {
          ...s,
          excluded,
          color: excluded ? ('error' as const) : ('primary' as const),
          variant: excluded
            ? ('outline' as const)
            : effective?.source === 'advertiser'
              ? ('subtle' as const)
              : ('outline' as const),
          title: excluded
            ? 'Excluded'
            : effective?.source === 'advertiser'
              ? 'Set on advertiser'
              : 'Inherited from brand',
        }
      })
      return chips.length
        ? h(
            'div',
            { class: 'flex flex-wrap gap-1' },
            chips.map((s) =>
              h(
                UBadge,
                {
                  color: s.color,
                  variant: s.variant,
                  size: 'sm',
                  title: s.title,
                  class: s.excluded ? 'line-through opacity-60' : '',
                },
                () => `${s.emoji ? s.emoji + ' ' : ''}${s.name}`,
              ),
            ),
          )
        : h('span', { class: 'text-muted' }, '-')
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 100,
    cell: ({ row }) =>
      h(
        UButton,
        {
          icon: 'i-lucide-settings',
          color: 'neutral',
          variant: 'ghost',
          size: 'sm',
          onClick: () => openSlideover(row.original),
        },
        () => 'Manage',
      ),
  },
]

async function loadAdvertisers() {
  const grouped = await admin.fetchAllAdvertisers()
  advertisers.value = [...grouped.values()].flat()
}

onMounted(async () => {
  window.addEventListener('hashchange', handleHashChange)
  try {
    await Promise.all([
      loadAdvertisers(),
      admin.fetchBrands(),
      admin.fetchPlatforms(),
      admin.fetchCountries(),
      admin.fetchCountryGroups(),
      admin.fetchSectors(),
    ])
    const hash = window.location.hash.slice(1)
    if (hash) openFromHash(hash)
  } catch (err) {
    handleError(err, 'Failed to load data')
  } finally {
    isLoading.value = false
  }
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})

watch(showSlideover, (isOpen) => {
  if (!isOpen && window.location.hash) {
    history.replaceState(null, '', window.location.pathname)
  }
})

function openFromHash(hash: string) {
  const row = rows.value.find((a) => a.id === hash)
  if (row) {
    openSlideover(row)
  } else {
    handleError(new Error('Advertiser not found'), 'Could not find advertiser')
    history.replaceState(null, '', window.location.pathname)
  }
}

function openSlideover(row: AdvertiserRow) {
  const isReplacing = showSlideover.value
  selected.value = row
  showSlideover.value = true
  if (isReplacing) {
    history.replaceState(null, '', `#${row.id}`)
  } else {
    history.pushState(null, '', `#${row.id}`)
  }
}

function closeSlideover() {
  showSlideover.value = false
  selected.value = null
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname)
  }
}

function handleHashChange() {
  const hash = window.location.hash.slice(1)
  if (!hash) {
    if (showSlideover.value) {
      showSlideover.value = false
      selected.value = null
    }
  } else if (selected.value?.id !== hash) {
    openFromHash(hash)
  }
}

function handleUpdated(adv: PlatformAdvertiser) {
  advertisers.value = advertisers.value.map((a) => (a.id === adv.id ? adv : a))
  if (selected.value?.id === adv.id) {
    selected.value = { ...selected.value, ...adv }
  }
}

async function refresh() {
  isLoading.value = true
  try {
    await Promise.all([loadAdvertisers(), admin.fetchBrands(), admin.fetchSectors()])
    // Update selected advertiser with fresh data if slideover is open
    // Use rows (not advertisers) to get enriched data including brand_sectors
    console.log('Refreshing advertisers, selected:', selected.value)
    if (selected.value) {
      const fresh = rows.value.find((a) => a.id === selected.value!.id)

      console.log('Refreshing selected advertiser', selected.value, fresh)
      if (fresh) {
        selected.value = fresh
      }
    }
  } catch (err) {
    handleError(err, 'Failed to refresh data')
  } finally {
    isLoading.value = false
  }
}

async function handleCreateAdvertiser(data: {
  brand_id: string
  platform_id: string
  platform_advertiser_identifier: string
  country_code: string | null
  sector_ids: string[]
}) {
  try {
    const adv = await admin.createBrandAdvertiser(data.brand_id, {
      platform_id: data.platform_id,
      platform_advertiser_identifier: data.platform_advertiser_identifier,
      country_code: data.country_code,
    })
    // Mark selected sectors as explicit, exclude unselected brand sectors
    const brand = admin.brands.find((b) => b.id === data.brand_id)
    const brandSectorIds = new Set(brand?.sectors.map((s) => s.id) ?? [])
    const selectedIds = new Set(data.sector_ids)
    const toExclude = [...brandSectorIds].filter((id) => !selectedIds.has(id))
    await Promise.all([
      ...data.sector_ids.map((id) => admin.addAdvertiserSector(data.brand_id, adv.id, id)),
      ...toExclude.map((id) => admin.excludeAdvertiserSector(data.brand_id, adv.id, id)),
    ])
    showCreateModal.value = false
    await loadAdvertisers()
    // Refresh brands in case a new sector was added to the brand
    await admin.fetchBrands()
    // Open the newly created advertiser
    const row = rows.value.find((r) => r.id === adv.id)
    if (row) openSlideover(row)
  } catch (err) {
    handleError(err, 'Failed to create advertiser')
  }
}
</script>

<template>
  <AppComponent title="Advertisers" id="admin-advertisers">
    <template #actions>
      <span class="text-sm text-muted">
        {{ filteredRows.length }} / {{ advertisers.length }} advertisers
      </span>
      <UButton
        icon="i-lucide-refresh-cw"
        color="neutral"
        variant="ghost"
        :loading="isLoading"
        @click="refresh"
      />
      <UButton icon="i-lucide-plus" color="primary" @click="showCreateModal = true">
        Create Advertiser
      </UButton>
    </template>

    <div class="h-full flex flex-col">
      <div class="p-4 space-y-2 shrink-0 bg-[var(--ui-bg)] border-b border-[var(--ui-border)]">
        <TriStateFilterButtons
          label="Filter by platform"
          :options="platformFilterOptions"
          :included="platformFilter.included.value"
          :excluded="platformFilter.excluded.value"
          @toggle="platformFilter.toggle"
          @clear="platformFilter.clear"
        />
        <TriStateFilterButtons
          label="Filter by sector"
          :options="sectorFilterOptions"
          :included="sectorFilter.included.value"
          :excluded="sectorFilter.excluded.value"
          @toggle="sectorFilter.toggle"
          @clear="sectorFilter.clear"
        />
        <div class="flex items-start gap-2">
          <button
            type="button"
            class="flex items-center gap-1 text-sm text-muted w-36 shrink-0 text-left pt-1"
            @click="showCountryFilter = !showCountryFilter"
          >
            <UIcon :name="showCountryFilter ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'" />
            Filter by country
          </button>
          <!-- Collapsed: just the active selection -->
          <div v-if="!showCountryFilter && selectedSummary.length" class="flex flex-wrap gap-1">
            <UButton
              v-for="chip in selectedSummary"
              :key="chip.key"
              color="primary"
              variant="soft"
              size="xs"
              trailing-icon="i-lucide-x"
              @click="removeChip(chip.codes)"
            >
              {{ chip.label }}
            </UButton>
            <UButton color="neutral" variant="ghost" size="xs" @click="selectedCountryCodes = []">
              Clear
            </UButton>
          </div>
        </div>
        <UPageCard v-if="showCountryFilter" variant="subtle" class="ml-38">
          <div class="flex items-center justify-between mb-1">
            <h3 class="text-sm font-medium">Regions</h3>
            <span class="text-xs text-muted">
              {{ selectedCountryCodes.length }} of {{ usedCountries.length }} selected
            </span>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            <label
              v-for="group in groupCoverage"
              :key="group.id"
              class="flex items-center gap-2 cursor-pointer text-sm hover:bg-muted/50 px-2 py-1 rounded"
              :class="{ 'bg-primary/10': group.selectedCount === group.totalCount }"
            >
              <input
                type="checkbox"
                :checked="group.selectedCount === group.totalCount"
                :indeterminate="group.selectedCount > 0 && group.selectedCount < group.totalCount"
                class="rounded border-gray-300 text-primary focus:ring-primary h-3 w-3"
                @change="toggleGroup(group)"
              />
              <span>{{ group.name }}</span>
              <span class="text-xs text-muted ml-auto">
                {{ group.selectedCount }}/{{ group.totalCount }}
              </span>
            </label>
          </div>

          <div class="mt-4 pt-3 border-t border-default flex items-center gap-4 mb-3">
            <h3 class="text-sm font-medium">Countries</h3>
            <UInput
              v-model="countrySearchQuery"
              placeholder="Search by name or code..."
              icon="i-lucide-search"
              :ui="countrySearchQuery ? activeFilterUi : undefined"
              size="sm"
              class="max-w-xs"
            />
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            <label
              v-for="country in filteredCountries"
              :key="country.code"
              class="flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm hover:bg-muted/50"
            >
              <input
                type="checkbox"
                :checked="selectedCountryCodes.includes(country.code)"
                class="rounded border-gray-300 text-primary focus:ring-primary h-3 w-3"
                @change="toggleCountry(country.code)"
              />
              <span>{{ country.emoji }}</span>
              <span class="truncate">{{ country.name }}</span>
            </label>
          </div>
        </UPageCard>
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted w-36 shrink-0">Search</span>
          <UInput
            v-model="searchQuery"
            icon="i-lucide-search"
            placeholder="Identifier or brand..."
            :ui="searchQuery ? activeFilterUi : undefined"
            size="sm"
            class="w-64"
          />
        </div>
      </div>

      <div class="flex-1 min-h-0 overflow-auto sticky-table-header">
        <UTable
          :columns="columns"
          :data="filteredRows"
          :loading="isLoading"
          v-model:sorting="sorting"
          v-model:pagination="pagination"
          :pagination-options="{ getPaginationRowModel: getPaginationRowModel() }"
        >
          <template #empty>
            <div class="text-center py-6 text-muted">
              {{ isLoading ? 'Loading data...' : 'No advertisers match the filters' }}
            </div>
          </template>
        </UTable>
      </div>

      <div
        v-if="filteredRows.length > pagination.pageSize"
        class="flex justify-center p-3 shrink-0 border-t border-[var(--ui-border)]"
      >
        <UPagination
          :page="pagination.pageIndex + 1"
          :items-per-page="pagination.pageSize"
          :total="filteredRows.length"
          @update:page="(p: number) => (pagination.pageIndex = p - 1)"
        />
      </div>
    </div>

    <USlideover v-model:open="showSlideover" side="right">
      <template #content>
        <AdminAdvertiserDetail
          :advertiser="selected"
          :on-refresh-all="refresh"
          @close="closeSlideover"
          @updated="handleUpdated"
        />
      </template>
    </USlideover>

    <UModal v-model:open="showCreateModal">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Create Advertiser</h3>
          </template>
          <AdminAdvertiserForm @submit="handleCreateAdvertiser" @cancel="showCreateModal = false" />
        </UCard>
      </template>
    </UModal>
  </AppComponent>
</template>
